import { inject, Injectable } from '@angular/core';
import { combineLatest, distinctUntilChanged, filter, map, Observable } from 'rxjs';
import { BaseMainTableDataService } from '@app/main/main-content/main-table/services/base-main-table-data.service';
import { LeakDetectionTabsPanelService } from '@app/main/leak-detection/services/leak-detection-tabs-panel/leak-detection-tabs-panel.service';
import { ILoggerNoiseTableData } from '@app/main/leak-detection/interfaces/logger-noise-table-data';
import { selectLoggerMeasurementEntities } from '@app/main/main-screen/store/data/logger-measurements/logger-measurements.selectors';
import { selectMainProjectId } from '@app/main/main-screen/store/main-selection/main-shared-selection/main-shared-selection.selectors';
import { selectProjectEntities } from '@app/main/main-screen/store/projects/projects.selectors';
import { LeakDetectionMode } from '@app/core/enums/leak-detection-mode.enum';
import { getLoggerMeasurementPrediction } from '@app/main/leak-detection/utils/logger-measurement-prediction';

@Injectable({
  providedIn: 'root',
})
export class LeakDetectionTableLoggerNoiseDataService extends BaseMainTableDataService<ILoggerNoiseTableData> {
  public leakDetectionTabsPanelService = inject(LeakDetectionTabsPanelService);

  public getData(): Observable<ILoggerNoiseTableData[]> {
    return combineLatest([
      this.leakDetectionTabsPanelService.loggers$,
      this.store.select(selectLoggerMeasurementEntities),
      // only the mode: the projects change on every selection save, and each change rebuilt every row
      combineLatest([this.store.select(selectMainProjectId), this.store.select(selectProjectEntities)]).pipe(
        map(([projectId, projectsMap]) => projectsMap[projectId!]?.leakDetectionMode ?? LeakDetectionMode.LeakScore),
        distinctUntilChanged()
      ),
    ]).pipe(
      filter(([loggers]) => loggers !== null),
      map(([loggers, loggerMeasurementsMap, leakDetectionMode]) => {
        return loggers.map(logger => {
          const { value: predictionScore, status: predictionStatus } = getLoggerMeasurementPrediction(
            logger.measurement,
            leakDetectionMode
          );

          return {
            id: logger.id,
            loggerType: logger.deployment.loggerType,
            predictionStatus,
            predictionScore,
            leakScore: logger.measurement?.leakScore ?? undefined,
            leakScoreStatus: logger.measurement?.leakScoreStatus ?? undefined,
            leakAiPredictor: logger.measurement?.leakAiPredictor ?? undefined,
            leakAiPredictorStatus: logger.measurement?.leakAiPredictorStatus ?? undefined,
            soundSignalUrl: logger.measurement?.soundSignalUrl,
            soundSignalId: logger.measurement?.soundSignalId,
            hasSoundSignal: !!logger.measurement?.soundSignalUrl,
            serialNo: logger.deployment.serialNo,
            lowestNoiseDecibel: logger.measurement?.lowestNoiseDecibel,
            spread: logger.measurement?.spread,
            measStartTime: logger.measurement?.measStartTime,
            soundSignalStartAt: logger.measurement?.soundSignalStartAt,
            assetId: logger.deployment.assetId || undefined,
            location: logger.deployment.location,
            comment: logger.deployment.comment?.content,
            coordinates: logger.deployment.coordinates,
            customCoordinates: logger.deployment.customCoordinates,
            envNoiseDecibel: logger.measurement?.envNoiseDecibel,
            eclForMeasurement: logger.deployment.eclForMeasurement,
            servingCellId: logger.deployment.servingCellId,
            raw: logger,
            channelAndCell: logger.measurement?.channelAndCell,
            roundTripTime: logger.deployment?.roundTripTime,
            usedBandForMeasurement: logger.deployment?.usedBandForMeasurement,
            scPci: logger.deployment?.scPci,
            scPciState: logger.deployment?.scPciState,
            cellTime: logger.measurement?.cellTime,
            syncTime: logger.measurement?.syncTime,
            connectionTime: logger.measurement?.connectionTime,
            soundSignalType: logger.measurement?.soundSignalType,
            samplingRate: loggerMeasurementsMap[logger.measurement?.id ?? '']?.samplingRate,
            ls: loggerMeasurementsMap[logger.measurement?.id ?? '']?.ls,
            dbOver: loggerMeasurementsMap[logger.measurement?.id ?? '']?.dbOver,
            fRated: loggerMeasurementsMap[logger.measurement?.id ?? '']?.fRated,
            fPeaks: loggerMeasurementsMap[logger.measurement?.id ?? '']?.fPeaks,
            dyn: loggerMeasurementsMap[logger.measurement?.id ?? '']?.dyn,
            periodicity: loggerMeasurementsMap[logger.measurement?.id ?? '']?.periodicity,
            facCorrels: loggerMeasurementsMap[logger.measurement?.id ?? '']?.facCorrels,
            l1: loggerMeasurementsMap[logger.measurement?.id ?? '']?.l1,
            l3: loggerMeasurementsMap[logger.measurement?.id ?? '']?.l3,
            l4: loggerMeasurementsMap[logger.measurement?.id ?? '']?.l4,
            l5: loggerMeasurementsMap[logger.measurement?.id ?? '']?.l5,
            l6: loggerMeasurementsMap[logger.measurement?.id ?? '']?.l6,
            l7: loggerMeasurementsMap[logger.measurement?.id ?? '']?.l7,
            cCount: loggerMeasurementsMap[logger.measurement?.id ?? '']?.cCount,
            spectrumStdDev: loggerMeasurementsMap[logger.measurement?.id ?? '']?.spectrumStdDev,
          };
        });
      })
    );
  }
}
